import React, { useEffect, useState } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  PointElement,
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  BarController,
  LineController
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import api from './services/api';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController
);

const Status = () => {
  const [nomesCount, setNomesCount] = useState(0);
  const [franquiaCount, setFranquiaCount] = useState(0);
  const [distribuicoesCount, setDistribuicoesCount] = useState(0);
  const [historicoDistribuicoes, setHistoricoDistribuicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Função para obter o ano e mês atual no formato YYYY-MM
  const getAnoMesAtual = () => {
    const data = new Date();
    const ano = data.getFullYear();
    // Adicionar 0 à esquerda para meses menores que 10
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${ano}-${mes}`;
  };

  // Função para obter os últimos 6 meses a partir do mês atual
  const getUltimos6Meses = () => {
    const meses = [];
    const data = new Date();
    
    for (let i = 0; i < 6; i++) {
      const ano = data.getFullYear();
      // Adicionar 0 à esquerda para meses menores que 10
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      
      meses.unshift(`${ano}-${mes}`); // Adiciona no início do array para manter ordem cronológica
      
      // Volta um mês
      data.setMonth(data.getMonth() - 1);
    }
    
    return meses;
  };

  // Função para formatar o ano_mes para exibição (YYYY-MM -> MM/YYYY)
  const formatarAnoMes = (anoMes) => {
    const [ano, mes] = anoMes.split('-');
    return `${mes}/${ano}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 1. Buscar total de nomes cadastrados
        const nomesResponse = await api.get('/client/nomes/count', { 
          params: { status: 'ATIVO' } 
        });
        setNomesCount(nomesResponse.data);
        
        // 2. Buscar dados da franquia
        try {
          const franquiaResponse = await api.get('/client/arrendatario');
          // Verificar se a resposta contém dados e o campo franquia
          if (franquiaResponse.data && franquiaResponse.data.franquia !== undefined) {
            setFranquiaCount(franquiaResponse.data.franquia);
          } else {
            setFranquiaCount(0);
          }
        } catch (franquiaError) {
          console.error('Erro ao buscar dados da franquia:', franquiaError);
          setFranquiaCount(0); // Em caso de erro, definir como 0
        }
        
        // 3. Buscar total de distribuições recebidas no mês atual
        const anoMesAtual = getAnoMesAtual();
        const distribuicoesResponse = await api.get('/client/distribuicoes/count', { 
          params: { ano_mes: anoMesAtual } 
        });
        setDistribuicoesCount(distribuicoesResponse.data);
        
        // 4. Buscar histórico de distribuições dos últimos 6 meses
        const ultimos6Meses = getUltimos6Meses();
        const historicoPromises = ultimos6Meses.map(anoMes => 
          api.get('/client/distribuicoes/count', { params: { ano_mes: anoMes } })
        );
        
        const historicoRespostas = await Promise.all(historicoPromises);
        const historicoData = ultimos6Meses.map((anoMes, index) => {
          const count = historicoRespostas[index].data;
          // Calcular o excedente (se houver)
          const excedente = Math.max(0, count - franquiaCount);
          
          return {
            anoMes: formatarAnoMes(anoMes),
            count: count,
            excedente: excedente
          };
        });
        
        setHistoricoDistribuicoes(historicoData);
        
      } catch (err) {
        console.error('Erro ao carregar dados de status:', err);
        setError('Ocorreu um erro ao carregar os dados. Por favor, tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [franquiaCount]);

  // Configuração do gráfico
  const chartData = {
    labels: historicoDistribuicoes.map(item => item.anoMes),
    datasets: [
      {
        type: 'bar',
        label: 'Processos',
        data: historicoDistribuicoes.map(item => item.count),
        backgroundColor: '#4CAF50',
        borderColor: '#45a049',
        borderWidth: 1,
        order: 1
      },
      {
        type: 'bar',
        label: 'Excedente',
        data: historicoDistribuicoes.map(item => item.excedente),
        backgroundColor: '#FF9800',
        borderColor: '#F57C00',
        borderWidth: 1,
        order: 0
      },
      {
        type: 'line',
        label: 'Franquia',
        data: historicoDistribuicoes.map(() => franquiaCount), // Valor fixo para todos os meses
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(54, 162, 235)',
        pointRadius: 4,
        fill: false,
        tension: 0.1,
        order: 2
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Histórico de Distribuições',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0 // Apenas números inteiros
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Carregando dados de status...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Painel</h1>
        <p className="text-sm text-muted-foreground mt-1">Status do serviço de distribuição</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-border p-5">
          <p className="text-sm text-muted-foreground">Nomes cadastrados</p>
          <p className="text-3xl font-bold text-foreground mt-1">{nomesCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-border p-5">
          <p className="text-sm text-muted-foreground">Franquia</p>
          <p className="text-3xl font-bold text-foreground mt-1">{franquiaCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-border p-5">
          <p className="text-sm text-muted-foreground">Distribuições recebidas no mês</p>
          <p className="text-3xl font-bold text-foreground mt-1">{distribuicoesCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-border p-5">
        <h2 className="text-base font-semibold text-foreground mb-4">Histórico</h2>
        <Chart type='bar' data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default Status;
